import { useCallback, useMemo, useState } from 'react';
import {
  Chip,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import { usePageTitle } from '../../components/shell/AppShell';
import { RouteDataGrid } from '../../components/data-grid/RouteDataGrid';
import type { QuickFilter, RowAction, SearchField } from '../../components/data-grid/RouteDataGrid';
import { ConfirmDialog } from '../../components/dialogs/ConfirmDialog';
import { fmtDateTime, fmtOrEmDash } from '../../utils/format';
import type { ExportColumn } from '../../utils/export';
import type { MachineSummary, Operator, SiteSummary } from '../../api/types';
import {
  useDecommissionMachine,
  useMachinesList,
  useMe,
  useMyPermissions,
  useOperatorsList,
  useReactivateMachine,
  selectableSites,
  useSitesList,
} from '../../api/queries';
import { MachineFormDialog } from './MachineFormDialog';
import { MachineDetailDialog } from './MachineDetailDialog';
import { MachineStatusChips } from './MachineStatusChips';

export default function MachinesPage() {
  usePageTitle('Machines');
  const me = useMe();
  const isArms = me.data?.is_arms ?? false;
  const perms = useMyPermissions();
  const has = useCallback(
    (code: string) => perms.data?.codes?.includes(code) ?? false,
    [perms.data?.codes],
  );

  const [operatorFilter, setOperatorFilter] = useState<string>('');
  const [siteFilter, setSiteFilter] = useState<string>('');
  const effectiveOperatorId = isArms ? (operatorFilter || null) : (me.data?.operator_id ?? null);

  const { queryKey, listFn } = useMachinesList({
    operatorId: effectiveOperatorId,
    siteId:     siteFilter || null,
  });

  const operators = useOperatorsList();
  const operatorsQ = useQuery({
    queryKey: operators.queryKey,
    queryFn: operators.listFn,
    enabled: isArms,
  });
  const operatorById = useMemo(() => {
    const m = new Map<string, Operator>();
    for (const o of operatorsQ.data ?? []) m.set(o.id, o);
    return m;
  }, [operatorsQ.data]);

  const sites = useSitesList(effectiveOperatorId);
  const sitesQ = useQuery({
    queryKey: sites.queryKey,
    queryFn: sites.listFn,
    enabled: !!effectiveOperatorId,
  });
  const siteById = useMemo(() => {
    const m = new Map<string, SiteSummary>();
    for (const s of sitesQ.data ?? []) m.set(s.id, s);
    return m;
  }, [sitesQ.data]);

  const [detailId, setDetailId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState<MachineSummary | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<{
    machine: MachineSummary; action: 'decommission' | 'reactivate';
  } | null>(null);
  const [decommReason, setDecommReason] = useState('');

  const decomm = useDecommissionMachine(
    confirmTarget?.machine.id ?? '00000000-0000-0000-0000-000000000000');
  const reactivate = useReactivateMachine(
    confirmTarget?.machine.id ?? '00000000-0000-0000-0000-000000000000');

  const columns = useMemo<GridColDef<MachineSummary>[]>(() => [
    { field: 'assetNumber', headerName: 'Asset', width: 140,
      renderCell: (p) => <code>{p.row.assetNumber}</code> },
    { field: 'make', headerName: 'Make / model', flex: 1.2,
      renderCell: (p) => (
        <Stack sx={{ py: 0.5 }}>
          <Typography variant="body2">{p.row.make} {p.row.model}</Typography>
          {p.row.gameName && (
            <Typography variant="caption" color="text.secondary">{p.row.gameName}</Typography>
          )}
        </Stack>
      ) },
    { field: 'siteId', headerName: 'Site', width: 220,
      renderCell: (p) => {
        // Prefer the name+code the list endpoint resolves on the row itself
        // (always present, even for an ARMS user browsing across operators);
        // fall back to the client-side sites map, then to a labelled marker.
        // Never render a bare GUID.
        const s = siteById.get(p.row.siteId);
        const name = p.row.siteName ?? s?.displayName ?? null;
        const code = p.row.siteCode ?? s?.code ?? null;
        if (!name) {
          return (
            <Typography variant="caption" color="text.secondary">
              — (site unavailable)
            </Typography>
          );
        }
        return (
          <Stack sx={{ py: 0.5 }}>
            <Typography variant="body2">{name}</Typography>
            {code && (
              <Typography variant="caption" color="text.secondary"
                sx={{ fontFamily: 'monospace' }}>{code}</Typography>
            )}
          </Stack>
        );
      } },
    ...(isArms ? [{
      field: 'operatorId', headerName: 'Operator', width: 180,
      renderCell: (p: { row: MachineSummary }) => {
        const op = operatorById.get(p.row.operatorId);
        return op ? `${op.displayName}` : p.row.operatorId.slice(0, 8) + '…';
      },
    } as GridColDef<MachineSummary>] : []),
    { field: 'ownershipKind', headerName: 'Ownership', width: 130,
      renderCell: (p) => <OwnershipChip kind={p.row.ownershipKind} /> },
    { field: 'protocol', headerName: 'Protocol', width: 110,
      renderCell: (p) => <code>{p.row.protocol}</code> },
    { field: 'status', headerName: 'Status', width: 130,
      renderCell: (p) => <StatusChip status={p.row.status} /> },
    { field: 'connectivity', headerName: 'Connectivity', width: 220, sortable: false,
      // Two-Status Reporting — the ESP32-link + machine-SAS chips.
      renderCell: (p) => <MachineStatusChips machine={p.row} /> },
    { field: 'updatedAt', headerName: 'Updated', width: 180,
      valueFormatter: (value) => fmtDateTime(value) },
  ], [isArms, operatorById, siteById]);

  const searchFields = useMemo<SearchField[]>(() => [
    { kind: 'text', key: 'assetNumber', label: 'Asset number' },
    { kind: 'text', key: 'make',        label: 'Make' },
    { kind: 'text', key: 'model',       label: 'Model' },
    { kind: 'text', key: 'gameName',    label: 'Game name' },
  ], []);

  const quickFilters = useMemo<QuickFilter<MachineSummary>[]>(() => [
    { label: 'All',             filter: () => true },
    { label: 'Active',          filter: (r) => r.status === 'active' },
    { label: 'Decommissioned',  filter: (r) => r.status === 'decommissioned' },
    { label: 'Operator-owned',  filter: (r) => r.ownershipKind === 'operator' },
    { label: 'Supplier-owned',  filter: (r) => r.ownershipKind === 'supplier' },
  ], []);

  const exportColumns = useMemo<ExportColumn<MachineSummary>[]>(() => [
    { key: 'assetNumber',          label: 'Asset Number' },
    { key: 'make',                 label: 'Make' },
    { key: 'model',                label: 'Model' },
    { key: 'gameName',             label: 'Game Name', format: (v) => fmtOrEmDash(v) },
    { key: 'siteName',             label: 'Site name',
      format: (_v, r) => r.siteName ?? siteById.get(r.siteId)?.displayName ?? '' },
    { key: 'siteCode',             label: 'Site code',
      format: (_v, r) => r.siteCode ?? siteById.get(r.siteId)?.code ?? '' },
    { key: 'operatorId',           label: 'Operator',
      format: (v) => operatorById.get(String(v))?.displayName ?? String(v) },
    { key: 'protocol',             label: 'Protocol' },
    { key: 'denominationCents',    label: 'Denom (¢)' },
    { key: 'rtpBasisPoints',       label: 'RTP (bps)', format: (v) => fmtOrEmDash(v) },
    { key: 'ownershipKind',        label: 'Ownership' },
    { key: 'cashlessMode',         label: 'Cashless mode' },
    { key: 'status',               label: 'Status' },
    { key: 'currentFirmwareVersion', label: 'Firmware', format: (v) => fmtOrEmDash(v) },
    { key: 'stickerSerial',        label: 'QR Code',    format: (v) => fmtOrEmDash(v) },
    { key: 'hatSerial',            label: 'HAT serial', format: (v) => fmtOrEmDash(v) },
    { key: 'createdAt',            label: 'Created',  format: (v) => fmtDateTime(v) },
    { key: 'updatedAt',            label: 'Updated',  format: (v) => fmtDateTime(v) },
    { key: 'decommissionedAt',     label: 'Decommissioned', format: (v) => fmtDateTime(v) },
  ], [operatorById, siteById]);

  const actions = useMemo<RowAction<MachineSummary>[]>(() => [
    { label: 'View',           onClick: (row) => setDetailId(row.id) },
    {
      label: 'Edit',
      visible: () => has('machines.edit'),
      onClick: (row) => setEditRow(row),
    },
    {
      label: 'Decommission',
      destructive: true,
      visible: (row) => has('machines.admin') && row.status === 'active',
      onClick: (row) => { setDecommReason(''); setConfirmTarget({ machine: row, action: 'decommission' }); },
    },
    {
      label: 'Reactivate',
      visible: (row) => has('machines.admin') && row.status === 'decommissioned',
      onClick: (row) => setConfirmTarget({ machine: row, action: 'reactivate' }),
    },
  ], [has]);

  return (
    <>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <Typography variant="h4" sx={{ flex: 1 }}>Machines</Typography>
          {isArms && (
            <TextField
              select size="small" label="Operator"
              value={operatorFilter}
              onChange={(e) => { setOperatorFilter(e.target.value); setSiteFilter(''); }}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="">All operators</MenuItem>
              {(operatorsQ.data ?? []).map((op) => (
                <MenuItem key={op.id} value={op.id}>{op.displayName}</MenuItem>
              ))}
            </TextField>
          )}
          {effectiveOperatorId && (
            <TextField
              select size="small" label="Site"
              value={siteFilter}
              onChange={(e) => setSiteFilter(e.target.value)}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="">All sites</MenuItem>
              {selectableSites(sitesQ.data).map((s) => (
                <MenuItem key={s.id} value={s.id}>{s.displayName}</MenuItem>
              ))}
            </TextField>
          )}
        </Stack>

        <RouteDataGrid<MachineSummary>
          entityName="Machines"
          queryKey={queryKey}
          listFn={listFn}
          columns={columns}
          searchFields={searchFields}
          quickFilters={quickFilters}
          exportColumns={exportColumns}
          reportTitle="ARMS — Machines"
          actions={actions}
          primaryAction={
            has('machines.edit')
              ? { label: '+ Create machine', onClick: () => setCreateOpen(true) }
              : undefined
          }
          onRowClick={(row) => setDetailId(row.id)}
          emptyMessage={isArms && !operatorFilter
            ? 'Pick an operator to view machines.'
            : 'No machines for this operator yet.'}
        />

        <MachineDetailDialog
          machineId={detailId}
          onClose={() => setDetailId(null)}
          onEdit={(detail) => {
            // MachineDetail is a superset of MachineSummary — every field on
            // the form is present, so we can pass it straight through.
            setDetailId(null);
            setEditRow(detail);
          }}
        />

        <MachineFormDialog
          open={createOpen}
          mode="create"
          onClose={() => setCreateOpen(false)}
        />

        <MachineFormDialog
          open={editRow !== null}
          mode="edit"
          machine={editRow ?? undefined}
          onClose={() => setEditRow(null)}
        />

        <ConfirmDialog
          open={confirmTarget !== null}
          title={confirmTarget?.action === 'decommission' ? 'Decommission machine' : 'Reactivate machine'}
          message={confirmTarget ? (
            confirmTarget.action === 'decommission' ? (
              <Stack spacing={1.5}>
                <Typography>
                  Decommission <strong>{confirmTarget.machine.assetNumber}</strong>?
                  This pulls the machine off the floor in ARMS; the site controller will
                  refuse new sessions.
                </Typography>
                <TextField
                  label="Reason (optional)"
                  size="small"
                  multiline minRows={2}
                  value={decommReason}
                  onChange={(e) => setDecommReason(e.target.value)}
                />
              </Stack>
            ) : (
              <>Reactivate <strong>{confirmTarget.machine.assetNumber}</strong>? It will accept sessions again.</>
            )
          ) : ''}
          confirmLabel={confirmTarget?.action === 'decommission' ? 'Decommission' : 'Reactivate'}
          destructive={confirmTarget?.action === 'decommission'}
          busy={decomm.isPending || reactivate.isPending}
          error={
            (confirmTarget?.action === 'decommission'
              ? (decomm.error as { message?: string } | null)?.message
              : (reactivate.error as { message?: string } | null)?.message) ?? undefined
          }
          onConfirm={() => {
            if (!confirmTarget) return;
            if (confirmTarget.action === 'decommission') {
              decomm.mutate(decommReason.trim() || null, {
                onSuccess: () => setConfirmTarget(null),
              });
            } else {
              reactivate.mutate(undefined, {
                onSuccess: () => setConfirmTarget(null),
              });
            }
          }}
          onCancel={() => setConfirmTarget(null)}
        />
      </Stack>
    </>
  );

}

function StatusChip({ status }: { status: string }) {
  const color = status === 'active' ? 'success'
              : status === 'decommissioned' ? 'default'
              : 'default';
  return <Chip size="small" label={status} color={color as 'success' | 'default'} />;
}

function OwnershipChip({ kind }: { kind: string }) {
  const label = kind === 'site_owner' ? 'site owner' : kind;
  const color = kind === 'operator' ? 'primary'
              : kind === 'supplier' ? 'info'
              : kind === 'site_owner' ? 'secondary' : 'default';
  return <Chip size="small" variant="outlined" label={label}
               color={color as 'primary' | 'info' | 'secondary' | 'default'} />;
}
